import type { Permission } from '@taskall/contracts';
import { LoadingSkeleton } from '@taskall/ui';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { homePath } from '@/components/layout/nav';
import { useAuth } from '@/features/auth/AuthProvider';
import { ForbiddenPage } from './ForbiddenPage';

export function FullPageLoader() {
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--taskall-background-secondary)]" aria-busy="true">
      <div className="w-72">
        <LoadingSkeleton rows={3} />
      </div>
    </div>
  );
}

/** Exige sessão; redireciona ao onboarding enquanto onboardingCompletedAt for null. */
export function RequireAuth() {
  const { status, session } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous' || !session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // Senha provisória: nada além da troca de senha até ela ser concluída.
  if (session.mustChangePassword && location.pathname !== '/trocar-senha') return <Navigate to="/trocar-senha" replace />;
  if (!session.mustChangePassword && location.pathname === '/trocar-senha') return <Navigate to={homePath(session)} replace />;
  if (!session.mustChangePassword && !session.onboardingCompleted && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

export function RequireAnonymous() {
  const { status, session } = useAuth();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'authenticated') return <Navigate to={homePath(session)} replace />;
  return <Outlet />;
}

export function RequirePermission({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <ForbiddenPage />;
}

/** Rota raiz: redireciona para a página inicial do perfil. */
export function HomeRedirect() {
  const { session } = useAuth();
  return <Navigate to={homePath(session)} replace />;
}
